import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Stack, Typography, useTheme } from "@mui/material";
import { GridCellParams } from "@mui/x-data-grid-premium";
import { useDefaultMaterialLimits } from "hooks/useDefaultMaterialLimits";
import { useDefaultTargetBaskets } from "hooks/useDefaultTargetBaskets";
import { useNotifyStatus } from "contexts/status";
import { useTenantTranslation } from "hooks/formatters";
import { useParameterFeasibility } from "hooks/useParameterFeasibility";
import { useTenant } from "hooks/settings";
import {
  CheckParameterFeasibilityRequest,
  useUpdateDefaultTargetBasketsMutation,
} from "src/store/api/generatedApi";
import { isFetchBaseQueryError } from "helpers";
import { GroupedProductionBasketsGrid } from "components/targetBaskets/GroupedProductionBasketsGrid";
import { useProductionBasketRowUpdater } from "components/targetBaskets/useProductionBasketRowUpdater";
import { ConflictWarningBanner } from "./ConflictWarningBanner";
import { useDefaultParametersContext } from "./context";
import { indexConflicts } from "./parameterConflicts";
import { ScrapChefDefaultsWarning } from "./ScrapChefDefaultsWarning";
import { useDefaultParameterVisit } from "./useDefaultParameterVisit";
import { useLimitLabelBySignature } from "./useLimitLabelBySignature";

interface Props {
  isActive: boolean;
  setConfirmOpen: (confirmOpen: boolean) => void;
}

export const TargetBasketsDefaultParameters = ({
  isActive,
  setConfirmOpen,
}: Props) => {
  const { setTabActions, hasMaterialLimitChanges } =
    useDefaultParametersContext();
  const theme = useTheme();
  const {
    rows,
    isLoading,
    isFetching,
    data,
    refetch,
    isError,
    showScrapchefWarning,
    scrapchefDefaultsByName,
  } = useDefaultTargetBaskets();
  const [editedValues, setEditedValues] = useState<Record<number, number>>({});
  const tenantName = useTenant();
  useDefaultParameterVisit({
    tenantName,
    page: "target_baskets",
    isActive,
  });
  const notifyStatus = useNotifyStatus();
  const { t } = useTenantTranslation();
  const [updateDefaults, { isLoading: isSaving }] =
    useUpdateDefaultTargetBasketsMutation();

  const originalValues = useMemo(
    () =>
      rows.reduce<Record<number, number>>((acc, row) => {
        acc[row.id] = row.defaultTargetNumBaskets;
        return acc;
      }, {}),
    [rows]
  );

  const tableRows: DefaultTargetBasketTableRow[] = useMemo(
    () =>
      rows.map((row) => ({
        id: row.id,
        groupName: row.name,
        productGroupId: row.productGroupId,
        productGroupName: row.productGroupName,
        steelGrades: row.steelGrades,
        target: editedValues[row.id] ?? row.defaultTargetNumBaskets,
      })),
    [rows, editedValues]
  );

  const hasChanges = Object.keys(editedValues).length > 0;
  const sourceChefGroupingId = data?.id ?? null;

  const { activeSet: persistedMaterialLimits, refetch: refetchMaterialLimits } =
    useDefaultMaterialLimits();
  const feasibilityRequest = useMemo<CheckParameterFeasibilityRequest | null>(
    () =>
      tableRows.length === 0 || persistedMaterialLimits === undefined
        ? null
        : {
            material_physics_overrides: null,
            material_chemistry_overrides: null,
            material_limits: persistedMaterialLimits.material_limits,
            target_baskets: tableRows.map((row) => ({
              chef_group_id: row.id,
              target_num_baskets: row.target,
            })),
          },
    [tableRows, persistedMaterialLimits]
  );
  const {
    conflicts,
    isChecking,
    hasError: feasibilityError,
  } = useParameterFeasibility(feasibilityRequest);
  const conflictChefGroupIds = useMemo(
    () => new Set(indexConflicts(conflicts).byChefGroup.keys()),
    [conflicts]
  );
  const chefGroupNameById = useMemo(
    () => Object.fromEntries(rows.map((row) => [row.id, row.name])),
    [rows]
  );
  const limitLabelBySignature = useLimitLabelBySignature();

  const onSave = useCallback(async () => {
    if (!sourceChefGroupingId || !hasChanges || hasMaterialLimitChanges) {
      setConfirmOpen(false);
      return;
    }
    const overrides = Object.entries(editedValues).map(
      ([chefGroupId, value]) => ({
        chef_group_id: Number(chefGroupId),
        target_num_baskets: value,
      })
    );
    try {
      await updateDefaults({
        tenantName,
        updateDefaultTargetBasketsRequest: {
          source_chef_grouping_id: sourceChefGroupingId,
          overrides,
        },
      }).unwrap();
      notifyStatus({
        text: t("defaultsSaved"),
        type: "success",
      });
      setEditedValues({});
      await Promise.all([refetch(), refetchMaterialLimits()]);
    } catch (error) {
      if (
        isFetchBaseQueryError(error) &&
        typeof error.status === "number" &&
        error.status === 409
      ) {
        notifyStatus({
          text: t("defaultsOutOfDate"),
          type: "error",
        });
        void refetch();
      } else {
        notifyStatus({
          text: t("errorSavingChanges"),
          type: "error",
        });
      }
    } finally {
      setConfirmOpen(false);
    }
  }, [
    editedValues,
    hasChanges,
    hasMaterialLimitChanges,
    notifyStatus,
    refetch,
    refetchMaterialLimits,
    sourceChefGroupingId,
    t,
    tenantName,
    updateDefaults,
    setConfirmOpen,
  ]);

  const onDiscard = useCallback(() => {
    setEditedValues({});
  }, []);

  useEffect(() => {
    if (!isActive) return;
    setTabActions({
      hasChanges,
      isChecking,
      isSaving,
      conflicts,
      onSave,
      onDiscard,
    });
    return () => {
      setTabActions(null);
    };
  }, [
    isActive,
    hasChanges,
    isChecking,
    isSaving,
    conflicts,
    onSave,
    onDiscard,
    setTabActions,
  ]);

  const getOriginalValue = useCallback(
    (row: DefaultTargetBasketTableRow) => originalValues[row.id] ?? row.target,
    [originalValues]
  );

  const handleValidValue = useCallback(
    (row: DefaultTargetBasketTableRow, value: number) => {
      setEditedValues((current) => {
        const original = originalValues[row.id];
        if (original === value) {
          return Object.fromEntries(
            Object.entries(current).filter(([k]) => k !== String(row.id))
          );
        }
        return { ...current, [row.id]: value };
      });
    },
    [originalValues, setEditedValues]
  );

  const handleProcessRowUpdate =
    useProductionBasketRowUpdater<DefaultTargetBasketTableRow>({
      getDefaultValue: getOriginalValue,
      onValidValue: handleValidValue,
    });

  const handleApplyScrapChefDefaults = useCallback(() => {
    const newEdits: Record<number, number> = {};
    rows.forEach((row) => {
      const scrapchefValue = scrapchefDefaultsByName[row.name];
      if (
        scrapchefValue !== undefined &&
        scrapchefValue !== row.defaultTargetNumBaskets
      ) {
        newEdits[row.id] = scrapchefValue;
      }
    });
    setEditedValues(newEdits);
  }, [rows, scrapchefDefaultsByName]);

  const editedProductGroups = useMemo(
    () =>
      productGroupsForChefGroups({
        rows: tableRows,
        chefGroupIds: new Set(Object.keys(editedValues).map(Number)),
      }),
    [tableRows, editedValues]
  );
  const conflictProductGroups = useMemo(
    () =>
      productGroupsForChefGroups({
        rows: tableRows,
        chefGroupIds: conflictChefGroupIds,
      }),
    [tableRows, conflictChefGroupIds]
  );

  const getCellClassName = useCallback(
    (params: GridCellParams<DefaultTargetBasketTableRow>) => {
      if (params.field !== "target") return "";

      const groupKey =
        params.rowNode.type === "group" ? params.rowNode.groupingKey : null;
      const isEdited =
        typeof params.id === "number"
          ? editedValues[params.id] !== undefined
          : typeof groupKey === "string" && editedProductGroups.has(groupKey);
      const hasConflict =
        typeof params.id === "number"
          ? conflictChefGroupIds.has(params.id)
          : typeof groupKey === "string" && conflictProductGroups.has(groupKey);
      return [isEdited && "edited-cell", hasConflict && "conflict-cell"]
        .filter(Boolean)
        .join(" ");
    },
    [
      editedValues,
      editedProductGroups,
      conflictChefGroupIds,
      conflictProductGroups,
    ]
  );

  return (
    <Stack gap={2} sx={{ flex: 1 }}>
      <Typography color="text.secondary">
        {t("productionBasketsDescription")}
      </Typography>
      {hasChanges && hasMaterialLimitChanges ? (
        <Alert severity="info">{t("saveMaterialLimitsBeforeBaskets")}</Alert>
      ) : null}
      <ScrapChefDefaultsWarning
        showWarning={showScrapchefWarning}
        onApplyDefaults={handleApplyScrapChefDefaults}
      />
      <ConflictWarningBanner
        hasError={feasibilityError}
        conflicts={conflicts}
        isChecking={isChecking}
        chefGroupNameById={chefGroupNameById}
        limitLabelBySignature={limitLabelBySignature}
      />
      {isError ? (
        <Alert severity="error">{t("errorLoadingData")}</Alert>
      ) : !isLoading && !isFetching && rows.length === 0 ? (
        <Typography color="text.secondary">{t("noChemistryGroups")}</Typography>
      ) : (
        <GroupedProductionBasketsGrid
          rows={tableRows}
          processRowUpdate={handleProcessRowUpdate}
          loading={isLoading || isFetching}
          defaultExpanded={false}
          getCellClassName={getCellClassName}
          sx={{
            flex: 1,
            "& .MuiDataGrid-cell[data-field='target']": {
              bgcolor: "action.hover",
            },
            "& .MuiDataGrid-cell.edited-cell": {
              bgcolor: theme.palette.warning.light,
              color: theme.palette.warning.contrastText,
            },
            "& .MuiDataGrid-cell.conflict-cell": {
              borderBottom: `2px solid ${theme.palette.error.main}`,
              "&:not(.edited-cell)": {
                bgcolor: theme.palette.error.light,
                color: theme.palette.error.contrastText,
              },
            },
          }}
        />
      )}
    </Stack>
  );
};

interface DefaultTargetBasketTableRow {
  id: number;
  groupName: string;
  productGroupId: number;
  productGroupName: string;
  steelGrades: string[];
  target: number;
}

const productGroupsForChefGroups = ({
  rows,
  chefGroupIds,
}: {
  rows: DefaultTargetBasketTableRow[];
  chefGroupIds: Set<number>;
}): Set<string> =>
  new Set(
    rows
      .filter((row) => chefGroupIds.has(row.id))
      .map((row) => row.productGroupName)
  );
